import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import AdDisplay from '../components/AdDisplay';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ForumThread = () => {
 const { threadId } = useParams();
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [thread, setThread] = useState(null);
 const [posts, setPosts] = useState([]);
 const [newPost, setNewPost] = useState('');
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchThread();
 fetchPosts();
 }, [threadId]);

 const fetchThread = async () => {
 try {
 const response = await axios.get(`${API}/forum/threads`);
 const foundThread = response.data.find(t => t.id === threadId);
 setThread(foundThread);
 } catch (error) {
 toast.error('Failed to load thread');
 } finally {
 setLoading(false);
 }
 };

 const fetchPosts = async () => {
 try {
 const response = await axios.get(`${API}/forum/posts`, { params: { thread_id: threadId } });
 setPosts(response.data);
 } catch (error) {
 toast.error('Failed to load posts');
 }
 };

 // Pick which reply indices should trailed by an inline ad. Runs
 // whenever the reply count changes so as more replies arrive we
 // sprinkle new ad breaks after them. Random gap = 4-7 replies so it
 // never feels metronomic. Set is memoized so the ad doesn't jitter
 // on unrelated re-renders.
 const adInsertAfter = useMemo(() => {
   const set = new Set();
   if (!posts || posts.length < 3) return set;
   let next = 3 + Math.floor(Math.random() * 4); // first ad after 3-6 replies
   while (next < posts.length) {
     set.add(next - 1); // insert AFTER the reply at (next-1)
     next += 4 + Math.floor(Math.random() * 4); // then every 4-7 more
   }
   return set;
 }, [posts.length]);

 const createPost = async () => {
 if (!newPost.trim()) {
 toast.error('Please enter a message');
 return;
 }

 try {
 await axios.post(`${API}/forum/posts`, {
 thread_id: threadId,
 content: newPost
 });
 setNewPost('');
 toast.success('Reply posted');
 fetchPosts();
 } catch (error) {
 toast.error('Failed to post reply');
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-background flex items-center justify-center">
 <p className="text-slate-500">Loading...</p>
 </div>
 );
 }

 if (!thread) {
 return (
 <div className="min-h-screen bg-background flex items-center justify-center">
 <Card>
 <CardContent className="pt-6">
 <p className="text-slate-500 mb-4">Thread not found</p>
 <Button onClick={() => navigate('/forum')} data-testid="back-to-forum-btn">Back to Forum</Button>
 </CardContent>
 </Card>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background">
 <div className="bg-white shadow-sm border-b">
 <div className="container mx-auto px-4 py-4 flex items-center gap-4">
 <Button variant="ghost" onClick={() => navigate('/forum')} data-testid="back-btn">
 <ArrowLeft className="w-5 h-5" />
 </Button>
 <h1 className="text-2xl font-bold truncate">{thread.title}</h1>
 </div>
 </div>

 <div className="container mx-auto px-4 py-8 max-w-4xl">
 <Card className="mb-6 shadow-lg">
 <CardHeader>
 <CardTitle>{thread.title}</CardTitle>
 <p className="text-sm text-slate-500">
 Started by {thread.user_email} • {new Date(thread.created_at).toLocaleDateString()}
 </p>
 </CardHeader>
 </Card>

 {/* Posts — with inline ads every 4–7 replies (randomized once per
     render so the ad doesn't jitter as new replies stream in). */}
 <div className="space-y-4 mb-6">
 {posts.map((post, i) => (
 <React.Fragment key={post.id}>
 <Card className="shadow-md" data-testid="forum-post">
 <CardContent className="pt-6">
 <div className="flex gap-4">
 <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
 <User className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <p className="font-medium">{post.user_email}</p>
 <span className="text-xs text-slate-500">
 {new Date(post.created_at).toLocaleString()}
 </span>
 </div>
 <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
 </div>
 </div>
 </CardContent>
 </Card>
 {adInsertAfter.has(i) && (
 <div className="flex justify-center py-2" data-testid={`forum-ad-after-${i}`}>
 <AdDisplay location="forum_between" />
 </div>
 )}
 </React.Fragment>
 ))}

 {posts.length === 0 && (
 <Card className="text-center py-8">
 <CardContent>
 <p className="text-slate-500">No replies yet. Be the first to comment!</p>
 </CardContent>
 </Card>
 )}
 </div>

 {/* Reply Form */}
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle>Post a Reply</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <Textarea
 value={newPost}
 onChange={(e) => setNewPost(e.target.value)}
 placeholder="Share your thoughts..."
 rows={4}
 data-testid="reply-textarea"
 />
 <Button onClick={createPost} className="w-full" data-testid="post-reply-btn">
 <Send className="w-4 h-4 mr-2" />
 Post Reply
 </Button>
 </CardContent>
 </Card>
 </div>
 </div>
 );
};

export default ForumThread;
